/* eslint-disable no-await-in-loop */
import { getFirebaseContext } from 'utils/firebase';
import { PublicationIssueSearchRequest } from 'lib/types/publicationIssue';
import {
  queryPublicationIssues,
  sortPublicationIssuesByDate
} from 'lib/services/publicationIssueService';
import {
  ENotice,
  EOrganization,
  ERef,
  ESnapshotExists,
  exists
} from 'lib/types';
import { getDateStringForDateInTimezone } from 'lib/utils/dates';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { logAndCaptureException } from 'utils';
import { PublicationIssueModel } from 'lib/model/objects/publicationIssueModel';
import { RunModel } from 'lib/model/objects/runModel';
import { UserNoticeModel } from 'lib/model/objects/userNoticeModel';
import { getModelFromSnapshot } from 'lib/model';
import moment from 'moment';
import { ColumnService } from 'lib/services/directory';
import { NoticeStatusType } from 'lib/enums';
import { isAffidavitDisabled } from 'lib/affidavits';
import { getBatchedInQueryResults } from 'lib/utils/firebase';

export type PublicationIssueAndRuns = {
  publicationIssue: PublicationIssueModel;
  verifiedRuns: RunModel[];
  unverifiedRuns: RunModel[];
  unverifiedNotices: UserNoticeModel[];
};

/**
 * Split an array into chunks of the given size.
 */
function chunk<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

export function useGetPublicationIssuesForAffidavits(
  activeOrganization: ESnapshotExists<EOrganization>,
  refreshTimestamp: Date
): {
  publicationIssuesLoading: boolean;
  publicationIssuesError: boolean;
  publicationIssuesAndRuns: PublicationIssueAndRuns[] | null;
} {
  const publicationDateTo = getDateStringForDateInTimezone({
    date: refreshTimestamp,
    timezone: activeOrganization.data().iana_timezone
  });
  const publicationDateFrom = getDateStringForDateInTimezone({
    date: moment(refreshTimestamp).subtract(3, 'months').toDate(),
    timezone: activeOrganization.data().iana_timezone
  });
  const publicationIssueQuery: PublicationIssueSearchRequest = {
    publisherIds: [activeOrganization.id],
    publicationDateTo,
    publicationDateFrom
  };

  const {
    value: publicationIssuesAndRuns,
    isError: publicationIssuesError,
    isLoading: publicationIssuesLoading
  } = useAsyncEffect({
    fetchData: async () => {
      const ctx = getFirebaseContext();
      const {
        response: publicationIssues,
        error: queryPublicationIssuesError
      } = await queryPublicationIssues(ctx, publicationIssueQuery);
      if (queryPublicationIssuesError || !publicationIssues) {
        throw (
          queryPublicationIssuesError ??
          new Error('No publication issues returned')
        );
      }

      // Step 1: Fetch ALL runs for all publication issues using batched 'in' queries.
      // Instead of 1 query per pub issue (~90 queries), we use Firestore 'in' operator
      // to query runs for up to 30 pub issues per query (~3 queries total).
      const allRunSnaps = await getBatchedInQueryResults(
        ctx.runsRef(),
        'publicationIssue',
        publicationIssues.map(pi => pi.ref)
      );

      // Filter out disabled/cancelled runs and convert to RunModels
      const allRunModels = allRunSnaps
        .map(snap => getModelFromSnapshot(RunModel, ctx, snap))
        .filter(run => !run.isDisabled() && !run.isCancelled());

      // Group runs by publication issue ID
      const runsByPubIssueId = new Map<string, RunModel[]>();
      for (const run of allRunModels) {
        const pubIssueId = run.modelData.publicationIssue.id;
        const existing = runsByPubIssueId.get(pubIssueId);
        if (existing) {
          existing.push(run);
        } else {
          runsByPubIssueId.set(pubIssueId, [run]);
        }
      }

      // Build pub issues that have at least one run
      const pubIssueMap = new Map(publicationIssues.map(pi => [pi.id, pi]));
      const pubIssuesWithRuns: {
        publicationIssue: PublicationIssueModel;
        runs: RunModel[];
        verifiedRuns: RunModel[];
        unverifiedRunNoticeIds: string[];
      }[] = [];

      const allUnverifiedNoticeRefsMap = new Map<string, ERef<ENotice>>();

      for (const [pubIssueId, runs] of runsByPubIssueId) {
        const publicationIssue = pubIssueMap.get(pubIssueId);
        if (!publicationIssue || runs.length === 0) continue;

        const verifiedRuns = runs.filter(run => run.isVerified());
        const unverifiedRunNoticeIdSet = new Set<string>();
        for (const run of runs) {
          if (!run.isVerified()) {
            const noticeRef = run.modelData.notice;
            allUnverifiedNoticeRefsMap.set(noticeRef.id, noticeRef);
            unverifiedRunNoticeIdSet.add(noticeRef.id);
          }
        }
        const unverifiedRunNoticeIds = Array.from(unverifiedRunNoticeIdSet);

        pubIssuesWithRuns.push({
          publicationIssue,
          runs,
          verifiedRuns,
          unverifiedRunNoticeIds
        });
      }

      // Step 2: Batch-fetch all unique unverified notices in small chunks with
      // limited parallelism. We cap concurrent .get() calls to stay under Firestore's
      // client-side ~100 connection limit (NOTICE_BATCH_SIZE × MAX_PARALLEL_CHUNKS).
      const NOTICE_BATCH_SIZE = 50;
      const MAX_PARALLEL_CHUNKS = 2;
      const uniqueNoticeRefs = Array.from(allUnverifiedNoticeRefsMap.values());
      const noticeRefChunks = chunk(uniqueNoticeRefs, NOTICE_BATCH_SIZE);
      const chunkGroups = chunk(noticeRefChunks, MAX_PARALLEL_CHUNKS);

      const allNoticeSnaps: Awaited<ReturnType<ERef<ENotice>['get']>>[] = [];
      for (const group of chunkGroups) {
        const groupResults = await Promise.all(
          group.map(refs =>
            Promise.all(
              refs.map(ref =>
                ref.get().catch(err => {
                  logAndCaptureException(
                    ColumnService.AFFIDAVITS,
                    err,
                    'Failed to fetch notice for publication issue',
                    { noticeId: ref.id }
                  );
                  return null;
                })
              )
            )
          )
        );
        for (const snaps of groupResults) {
          for (const snap of snaps) {
            if (snap) allNoticeSnaps.push(snap);
          }
        }
      }

      // Step 3: Filter invalid notices and check affidavit status.
      // Pre-fetch all unique newspapers in parallel (typically 1-2 per org).
      const uniqueNewspaperRefs = [
        ...new Map(
          allNoticeSnaps
            .filter(exists)
            .map(n => [n.data().newspaper.id, n.data().newspaper])
        ).values()
      ];
      const newspaperSnaps = await Promise.all(
        uniqueNewspaperRefs.map(ref => ref.get())
      );
      const newspaperCache = new Map(
        uniqueNewspaperRefs.map((ref, i) => [ref.id, newspaperSnaps[i]])
      );

      const validNoticeSnapMap = new Map<string, ESnapshotExists<ENotice>>();

      for (const notice of allNoticeSnaps) {
        if (!exists(notice)) continue;

        const { noticeStatus } = notice.data();
        if (
          [
            NoticeStatusType.cancelled.value,
            NoticeStatusType.affidavit_submitted.value
          ].includes(noticeStatus)
        ) {
          continue;
        }

        const newspaper = newspaperCache.get(notice.data().newspaper.id);
        if (!newspaper || isAffidavitDisabled(notice.data(), newspaper))
          continue;

        validNoticeSnapMap.set(notice.id, notice);
      }

      // Step 4: Assemble the final result using pre-fetched data (no more Firestore calls)
      const sortedResults: PublicationIssueAndRuns[] = [];
      for (const entry of pubIssuesWithRuns) {
        const unverifiedNotices: UserNoticeModel[] = [];
        for (const id of entry.unverifiedRunNoticeIds) {
          const snap = validNoticeSnapMap.get(id);
          if (snap) {
            unverifiedNotices.push(
              getModelFromSnapshot(UserNoticeModel, ctx, snap)
            );
          }
        }

        const unverifiedRuns: RunModel[] = [];
        for (const notice of unverifiedNotices) {
          const run = entry.runs.find(r => r.modelData.notice.id === notice.id);
          if (run) {
            unverifiedRuns.push(run);
          }
        }

        sortedResults.push({
          publicationIssue: entry.publicationIssue,
          verifiedRuns: entry.verifiedRuns,
          unverifiedRuns,
          unverifiedNotices
        });
      }

      return sortPublicationIssuesByDate(sortedResults, 'desc');
    },
    dependencies: [activeOrganization.id, refreshTimestamp.valueOf()]
  });

  return {
    publicationIssuesLoading,
    publicationIssuesError,
    publicationIssuesAndRuns
  };
}
